import { useState } from 'react';
import { motion } from 'framer-motion';
import { Check, ChevronLeft, ChevronRight, GitBranch, Workflow, Eye } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { MiniDiagramPreview } from './MiniDiagramPreview';
import { InteractiveDiagramViewer } from './InteractiveDiagramViewer';
import type { Template } from '@/shared/workflowAITypes';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';

interface TemplateCarouselProps {
  templates: Template[];
  onSelect: (templateId: string) => void;
  disabled?: boolean;
}

export function TemplateCarousel({ templates, onSelect, disabled }: TemplateCarouselProps) {
  const [currentIdx, setCurrentIdx] = useState(0);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [previewTemplate, setPreviewTemplate] = useState<Template | null>(null);

  const prev = () => setCurrentIdx(i => Math.max(0, i - 1));
  const next = () => setCurrentIdx(i => Math.min(templates.length - 1, i + 1));

  const handleSelect = (id: string) => {
    setSelectedId(id);
    onSelect(id);
  };

  const handleClosePreview = () => {
    setPreviewTemplate(null);
  };

  return (
    <div className="space-y-3">
      <p className="text-xs font-medium text-slate-800 uppercase tracking-wider px-1">
        Choose a template
      </p>

      {/* Carousel container */}
      <div className="relative">
        {/* Navigation arrows */}
        {templates.length > 1 && (
          <>
            <button
              onClick={prev}
              disabled={currentIdx === 0 || disabled}
              className={cn(
                'absolute -left-2 top-1/2 -translate-y-1/2 z-10 w-7 h-7 rounded-full bg-white/90 border border-slate-200 shadow-sm flex items-center justify-center transition-all',
                'hover:bg-white hover:shadow-md',
                (currentIdx === 0 || disabled) && 'opacity-0 pointer-events-none',
              )}
            >
              <ChevronLeft className="h-4 w-4 text-slate-600" />
            </button>
            <button
              onClick={next}
              disabled={currentIdx === templates.length - 1 || disabled}
              className={cn(
                'absolute -right-2 top-1/2 -translate-y-1/2 z-10 w-7 h-7 rounded-full bg-white/90 border border-slate-200 shadow-sm flex items-center justify-center transition-all',
                'hover:bg-white hover:shadow-md',
                (currentIdx === templates.length - 1 || disabled) && 'opacity-0 pointer-events-none',
              )}
            >
              <ChevronRight className="h-4 w-4 text-slate-600" />
            </button>
          </>
        )}

        {/* Cards track */}
        <div className="overflow-hidden rounded-xl">
          <motion.div
            className="flex"
            animate={{ x: `${-currentIdx * 100}%` }}
            transition={{ type: 'spring', stiffness: 300, damping: 30 }}
          >
            {templates.map(tmpl => {
              const isSelected = selectedId === tmpl.id;
              const nodeCount = tmpl.diagramSpec.nodes.length;
              const edgeCount = tmpl.diagramSpec.edges.length;

              return (
                <div key={tmpl.id} className="w-full flex-shrink-0 px-1">
                  <div
                    className={cn(
                      'rounded-xl border p-4 transition-all duration-200',
                      isSelected
                        ? 'border-workfinity-400 bg-workfinity-50/80 shadow-md ring-1 ring-workfinity-200'
                        : 'border-slate-200 bg-white/70 hover:border-slate-300 hover:shadow-sm',
                      disabled && 'opacity-60',
                    )}
                  >
                    {/* Header */}
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h4 className="text-sm font-semibold text-slate-800">{tmpl.title}</h4>
                        <p className="text-xs text-slate-800 mt-0.5 leading-relaxed">
                          {tmpl.thumbnailSummary}
                        </p>
                      </div>
                      <div className="flex-shrink-0 w-9 h-9 rounded-lg bg-gradient-to-br from-workfinity-100 to-workfinity-200 flex items-center justify-center">
                        <Workflow className="h-4 w-4 text-workfinity-600" />
                      </div>
                    </div>

                    {/* Workflow Diagram Preview
                    {tmpl.wfJson && (
                      <MiniDiagramPreview
                        wfJson={tmpl.wfJson}
                        height={150}
                        className="mb-3"
                      />
                    )}
                    */}

                    {/* Diagram stats */}
                    <div className="flex items-center gap-3 text-xs text-slate-400 mb-3 border-t border-slate-100 pt-2.5">
                      <span className="flex items-center gap-1">
                        <span className="w-2 h-2 rounded-full bg-emerald-400" />
                        {nodeCount} node{nodeCount !== 1 ? 's' : ''}
                      </span>
                      <span className="flex items-center gap-1">
                        <GitBranch className="h-3 w-3" />
                        {edgeCount} edge{edgeCount !== 1 ? 's' : ''}
                      </span>
                    </div>

                    {/* Select & View buttons */}
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => window.open(`https://workflow-designer.workfinity.com/workflow/template/${tmpl.id}`, '_blank', 'noopener,noreferrer')}
                        className="flex-1 border-slate-200 hover:bg-slate-50 text-slate-700 hover:text-slate-900"
                      >
                        <Eye className="h-3.5 w-3.5 mr-1.5" />
                        Preview Mode
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => handleSelect(tmpl.id)}
                        disabled={disabled || isSelected}
                        className={cn(
                          'flex-1 transition-all',
                          isSelected
                            ? 'bg-workfinity-600 text-white cursor-default'
                            : 'bg-gradient-to-r from-workfinity-500 to-workfinity-600 hover:from-workfinity-600 hover:to-workfinity-700 text-white',
                        )}
                      >
                        {isSelected ? (
                          <>
                            <Check className="h-3.5 w-3.5 mr-1.5" />
                            Selected
                          </>
                        ) : (
                          'Use'
                        )}
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </motion.div>
        </div>

        {/* Dots indicator */}
        {templates.length > 1 && (
          <div className="flex justify-center gap-1.5 mt-3">
            {templates.map((_, i) => (
              <button
                key={i}
                onClick={() => setCurrentIdx(i)}
                className={cn(
                  'w-1.5 h-1.5 rounded-full transition-all duration-200',
                  i === currentIdx
                    ? 'bg-workfinity-500 w-4'
                    : 'bg-slate-300 hover:bg-slate-400',
                )}
              />
            ))}
          </div>
        )}
      </div>

      {/* Zoomable Preview Dialog (Commented for future reuse)
      <Dialog open={!!previewTemplate} onOpenChange={(open) => !open && handleClosePreview()}>
        <DialogContent minWidth="sm:min-w-[850px]" maxWidth="max-w-4xl" className="p-6">
          {previewTemplate && (
            <>
              <DialogHeader className="mb-4">
                <div className="flex items-center gap-2.5">
                  <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-workfinity-100 to-workfinity-200 flex items-center justify-center">
                    <Workflow className="h-5 w-5 text-workfinity-600" />
                  </div>
                  <div>
                    <DialogTitle className="text-lg font-bold text-slate-900">
                      {previewTemplate.title}
                    </DialogTitle>
                    <DialogDescription className="text-sm text-slate-500 mt-0.5">
                      {previewTemplate.thumbnailSummary || 'Detailed workflow template view'}
                    </DialogDescription>
                  </div>
                </div>
              </DialogHeader>

              
              {previewTemplate.wfJson || previewTemplate.diagramSpec ? (
                <InteractiveDiagramViewer
                  wfJson={previewTemplate.wfJson || JSON.stringify(previewTemplate.diagramSpec)}
                  height={500}
                  className="w-full mb-2"
                />
              ) : (
                <div className="h-[300px] flex items-center justify-center text-slate-400 text-sm border border-slate-200/80 rounded-xl mb-2">
                  No diagram spec available
                </div>
              )}

              <div className="flex items-center justify-between mt-6 border-t pt-4">
                <div className="flex items-center gap-2">
                  <Button 
                    variant="outline" 
                    size="icon"
                    onClick={() => {
                      const idx = templates.findIndex(t => t.id === previewTemplate.id);
                      if (idx > 0) setPreviewTemplate(templates[idx - 1]);
                    }}
                    disabled={templates.findIndex(t => t.id === previewTemplate.id) <= 0}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <Button 
                    variant="outline" 
                    size="icon"
                    onClick={() => {
                      const idx = templates.findIndex(t => t.id === previewTemplate.id);
                      if (idx < templates.length - 1) setPreviewTemplate(templates[idx + 1]);
                    }}
                    disabled={templates.findIndex(t => t.id === previewTemplate.id) >= templates.length - 1}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
                <div className="flex gap-3">
                  <Button variant="outline" onClick={handleClosePreview}>
                    Close
                  </Button>
                  <Button
                    onClick={() => {
                      handleSelect(previewTemplate.id);
                      handleClosePreview();
                    }}
                    disabled={disabled || selectedId === previewTemplate.id}
                    className="bg-gradient-to-r from-workfinity-500 to-workfinity-600 hover:from-workfinity-600 hover:to-workfinity-700 text-white"
                  >
                    {selectedId === previewTemplate.id ? (
                      <>
                        <Check className="h-4 w-4 mr-1.5" />
                        Selected
                      </>
                    ) : (
                      'Use This Template'
                    )}
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
      */}
    </div>
  );
}
